import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../utils/api';

interface AuthContextType {
  encryptedUserId: string | null;
  portalId: number | null;
  userInfo: {
    email: string;
    portalId: number;
  } | null;
  isLoading: boolean;
  error: string | null;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const [encryptedUserId, setEncryptedUserId] = useState<string | null>(null);
  const [portalId, setPortalId] = useState<number | null>(null);
  const [userInfo, setUserInfo] = useState<{ email: string; portalId: number } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshAuth = async () => {
    const currentUserId = encryptedUserId || searchParams.get('userId') || localStorage.getItem('encryptedUserId');
    
    if (!currentUserId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const authStatus = await apiClient.getAuthenticationStatus(currentUserId);
      
      if (authStatus.success) {
        setEncryptedUserId(currentUserId);
        setPortalId(authStatus.user.portalId);
        setUserInfo(authStatus.user);
        localStorage.setItem('encryptedUserId', currentUserId);
      } else {
        setError(authStatus.error || 'Authentication failed');
        setEncryptedUserId(null);
        setPortalId(null);
        setUserInfo(null);
        localStorage.removeItem('encryptedUserId');
      }
    } catch (err) {
      setError('Failed to authenticate user');
      setEncryptedUserId(null);
      setPortalId(null);
      setUserInfo(null);
      localStorage.removeItem('encryptedUserId');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshAuth();
  }, [searchParams]);

  const value: AuthContextType = {
    encryptedUserId,
    portalId,
    userInfo,
    isLoading,
    error,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
